(() => {
  'use strict';
  if (window.LRLCartDrawer) return;
  const drawer = document.getElementById('halo-cart-sidebar');
  const configNode = document.getElementById('lrl-cart-drawer-config');
  if (!drawer || !configNode) return;
  const config = JSON.parse(configNode.textContent);
  const wrapper = drawer.querySelector('.previewCart-wrapper');
  if (!wrapper) return;
  const text = (key, product = '') => String(config.labels[key] || '').replace('__product__', product);
  const overlay = document.createElement('button');
  overlay.type = 'button';
  overlay.className = 'lrl-cart-overlay';
  overlay.tabIndex = -1;
  overlay.setAttribute('aria-label', text('close'));
  overlay.hidden = true;
  document.body.append(overlay);
  const status = document.createElement('div');
  status.className = 'lrl-cart-status';
  status.setAttribute('role', 'status');
  status.setAttribute('aria-live', 'polite');
  status.hidden = true;
  drawer.querySelector('.halo-sidebar-header').after(status);
  const retry = document.createElement('button');
  retry.type = 'button';
  retry.className = 'lrl-cart-retry';
  retry.textContent = text('retry');
  retry.hidden = true;
  status.after(retry);
  const page = document.querySelector('.body-content-wrapper');
  let opened = false;
  let busy = false;
  let trusted = false;
  let returnFocus = null;
  let currentCart = null;
  let refreshPromise = null;
  let refreshAgain = false;
  let mutationPromise = null;
  let notice = '';
  let pendingFocus = null;
  let pendingInput = null;
  let quantityTimer = null;
  let restorePageInert = false;
  drawer.classList.add('lrl-cart-drawer');
  drawer.setAttribute('role', 'dialog');
  drawer.setAttribute('aria-modal', 'true');
  drawer.setAttribute('aria-label', text('title'));
  drawer.setAttribute('aria-hidden', 'true');
  drawer.inert = true;
  drawer.tabIndex = -1;
  document.querySelectorAll('[data-lrl-v2-cart-link]').forEach(node => {
    node.setAttribute('aria-haspopup', 'dialog');
    node.setAttribute('aria-controls', drawer.id);
    node.setAttribute('aria-expanded', 'false');
  });
  const announce = (message, error = false) => {
    status.textContent = message;
    status.hidden = !message;
    status.classList.toggle('is-error', error);
    retry.hidden = !error;
  };
  const applyBusy = () => {
    drawer.setAttribute('aria-busy', String(busy));
    drawer.querySelectorAll('[data-cart-remove], .btn-quantity, [data-cart-quantity]').forEach(node => { node.disabled = busy; });
    const checkout = drawer.querySelector('#cart-sidebar-checkout');
    if (checkout) {
      const terms = drawer.querySelector('#sidebar_cart_conditions');
      checkout.disabled = busy || !!pendingInput || !trusted || !currentCart?.item_count || !!(terms && !terms.checked);
    }
  };
  const updateCount = cart => {
    if (typeof cart?.item_count !== 'number') return;
    currentCart = cart;
    document.querySelectorAll('[data-cart-count], [data-lrl-v2-cart-count]').forEach(node => { node.textContent = String(cart.item_count); });
    document.querySelectorAll('[data-cart-text]').forEach(node => { node.textContent = text(cart.item_count === 1 ? 'item' : 'items'); });
    document.dispatchEvent(new CustomEvent('lrl:cart-updated', {detail: cart}));
  };
  const enhance = () => {
    drawer.querySelectorAll('.previewCartItem').forEach(row => {
      const title = row.querySelector('.previewCartItem-name')?.textContent.trim() || '';
      row.querySelectorAll('.btn-quantity').forEach(node => {
        let button = node;
        if (node.tagName !== 'BUTTON') {
          button = document.createElement('button');
          for (const {name, value} of [...node.attributes]) if (!['role', 'aria-disabled', 'href'].includes(name)) button.setAttribute(name, value);
          node.replaceWith(button);
        }
        button.type = 'button';
        button.setAttribute('aria-label', text(button.classList.contains('plus') ? 'increase' : 'decrease', title));
        if (!button.textContent) button.textContent = button.classList.contains('plus') ? '+' : '−';
      });
      const input = row.querySelector('[data-cart-quantity]');
      if (input) {
        input.setAttribute('aria-label', text('quantity', title));
        input.min = '0';
        input.step = '1';
        input.inputMode = 'numeric';
      }
    });
    drawer.querySelectorAll('.cartTool-item[data-popup]').forEach(node => {
      const panel = drawer.querySelector(`#${CSS.escape(node.dataset.popup)}`);
      const label = panel?.querySelector('label')?.textContent.trim();
      if (label) node.setAttribute('aria-label', label);
      node.setAttribute('role', 'button');
      node.tabIndex = 0;
    });
    const checkout = drawer.querySelector('#cart-sidebar-checkout');
    if (checkout) {
      checkout.type = 'button';
      if (checkout.textContent.trim() !== text('proceed')) checkout.textContent = text('proceed');
    }
    drawer.querySelectorAll('.previewCart-emptyBody .button-continue').forEach(node => {
      if (!node.hasAttribute('href') && !node.hasAttribute('onclick')) {
        node.setAttribute('href', config.continueUrl);
        node.removeAttribute('aria-disabled');
      }
    });
    applyBusy();
  };
  const focusClose = () => drawer.querySelector('[data-close-cart-sidebar]')?.focus({preventScroll: true});
  const syncOpen = () => {
    const next = document.body.classList.contains('cart-sidebar-show');
    if (opened === next) return;
    opened = next;
    drawer.inert = !opened;
    drawer.setAttribute('aria-hidden', String(!opened));
    overlay.hidden = !opened;
    document.querySelectorAll('[data-lrl-v2-cart-link]').forEach(node => node.setAttribute('aria-expanded', String(opened)));
    if (opened) {
      if (!returnFocus || !returnFocus.isConnected) returnFocus = document.activeElement;
      restorePageInert = page?.inert || false;
      if (page) page.inert = true;
      document.documentElement.classList.add('lrl-cart-open');
      requestAnimationFrame(() => { if (opened) focusClose(); });
      refresh();
    } else {
      if (page) page.inert = restorePageInert;
      document.documentElement.classList.remove('lrl-cart-open');
    }
  };
  const open = trigger => {
    if (!opened) returnFocus = trigger?.isConnected ? trigger : document.activeElement;
    document.body.classList.add('cart-sidebar-show');
    syncOpen();
    if (opened && !refreshPromise) refresh();
  };
  const close = (restore = true) => {
    document.body.classList.remove('cart-sidebar-show');
    syncOpen();
    if (restore && returnFocus?.isConnected) returnFocus.focus({preventScroll: true});
    returnFocus = null;
  };
  const request = async (url, options = {}) => {
    const abort = new AbortController();
    const timer = setTimeout(() => abort.abort(), 15000);
    try {
      const response = await fetch(url, {...options, credentials: 'same-origin', signal: abort.signal});
      if (!response.ok) {
        let error;
        try { error = await response.json(); } catch {}
        throw new Error(error?.description || (typeof error?.message === 'string' ? error.message : '') || text('error'));
      }
      return response;
    } finally { clearTimeout(timer); }
  };
  const htmlUrl = () => {
    const url = new URL(config.cartUrl, location.origin);
    url.searchParams.set('view', 'ajax_side_cart');
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    return url.href;
  };
  const refresh = () => {
    if (mutationPromise) { refreshAgain = true; return mutationPromise; }
    if (refreshPromise) { refreshAgain = true; return refreshPromise; }
    clearTimeout(quantityTimer);
    quantityTimer = null;
    pendingInput = null;
    busy = true;
    applyBusy();
    if (!notice) announce(text('loading'));
    refreshPromise = (async () => {
      try {
        const [cartResponse, htmlResponse] = await Promise.all([
          request(`${config.cartUrl}.js`, {cache: 'no-store', headers: {Accept: 'application/json'}}),
          request(htmlUrl(), {cache: 'no-store', headers: {Accept: 'text/html'}})
        ]);
        const [cart, html] = await Promise.all([cartResponse.json(), htmlResponse.text()]);
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        if (!parsed.querySelector('.previewCart')) throw new Error(text('error'));
        parsed.querySelectorAll('script').forEach(node => node.remove());
        const active = drawer.contains(document.activeElement) ? document.activeElement : null;
        const activeKey = active?.closest('.previewCartItem')?.querySelector('[data-line]')?.dataset.line;
        const activePart = active?.matches('[data-cart-remove]') ? '[data-cart-remove]' : active?.matches('[data-cart-quantity]') ? '[data-cart-quantity]' : active?.matches('.plus') ? '.plus' : active?.matches('.minus') ? '.minus' : null;
        wrapper.replaceChildren(...parsed.body.childNodes);
        trusted = true;
        updateCount(cart);
        enhance();
        if (activePart && opened) {
          const row = [...drawer.querySelectorAll('.previewCartItem')].find(row => row.querySelector('[data-line]')?.dataset.line === activeKey);
          pendingFocus = row?.querySelector(activePart) || drawer.querySelector('[data-close-cart-sidebar]');
        } else if (active && !active.isConnected && opened) {
          pendingFocus = active.id ? drawer.querySelector(`#${CSS.escape(active.id)}`) : null;
          pendingFocus ||= drawer.querySelector('[data-close-cart-sidebar]');
        }
        announce(notice, !!notice);
      } catch (error) {
        trusted = false;
        announce(error.name === 'AbortError' ? text('error') : error.message || text('error'), true);
      } finally {
        busy = false;
        refreshPromise = null;
        applyBusy();
        if (opened && pendingFocus?.isConnected) pendingFocus.focus({preventScroll: true});
        if (opened && !drawer.contains(document.activeElement)) focusClose();
        pendingFocus = null;
        if (refreshAgain) { refreshAgain = false; refresh(); }
      }
    })();
    return refreshPromise;
  };
  const change = async (input, quantity) => {
    if (busy || mutationPromise) return;
    if (!Number.isInteger(quantity) || quantity < 0) { notice = text('error'); await refresh(); return; }
    const key = input.dataset.line;
    if (!key) return;
    busy = true;
    trusted = false;
    notice = '';
    applyBusy();
    announce(text('loading'));
    mutationPromise = (async () => {
      try {
        const response = await request(config.changeUrl, {method: 'POST', headers: {'Content-Type': 'application/json', Accept: 'application/json'}, body: JSON.stringify({id: key, quantity})});
        const cart = await response.json();
        if (cart.errors) throw new Error(typeof cart.errors === 'string' ? cart.errors : text('error'));
        updateCount(cart);
      } catch (error) {
        notice = error.name === 'AbortError' ? text('error') : error.message || text('error');
      }
    })();
    await mutationPromise;
    mutationPromise = null;
    refreshAgain = false;
    await refresh();
  };
  document.addEventListener('click', event => {
    const target = event.target.closest?.('button, a');
    if (!target) return;
    if (target.matches('[data-lrl-v2-cart-link], [data-cart-sidebar]')) {
      event.preventDefault(); event.stopImmediatePropagation(); open(target); return;
    }
    if (target === overlay || drawer.contains(target) && target.matches('[data-close-cart-sidebar]')) {
      event.preventDefault(); event.stopImmediatePropagation(); close(); return;
    }
    if (!drawer.contains(target)) return;
    if (target.matches('[data-open-edit-cart]')) { close(false); return; }
    if (target.matches('[data-cart-remove], .btn-quantity')) {
      event.preventDefault(); event.stopImmediatePropagation();
      const input = target.closest('.previewCartItem')?.querySelector('[data-cart-quantity]');
      if (!input) return;
      const quantity = target.matches('[data-cart-remove]') ? 0 : Math.max(0, Number(input.value) + (target.classList.contains('plus') ? 1 : -1));
      change(input, quantity);
    } else if (target.matches('#cart-sidebar-checkout, .magic-cart-clone-btn')) {
      if (busy || pendingInput || !trusted) { event.preventDefault(); event.stopImmediatePropagation(); return; }
      setTimeout(() => close(false), 0);
    }
  }, true);
  document.addEventListener('input', event => {
    if (!drawer.contains(event.target) || !event.target.matches('[data-cart-quantity]') || busy) return;
    clearTimeout(quantityTimer);
    pendingInput = event.target;
    applyBusy();
    if (pendingInput.value.trim() !== '') quantityTimer = setTimeout(() => {
      const input = pendingInput;
      pendingInput = null;
      quantityTimer = null;
      if (input?.isConnected) change(input, Number(input.value));
    }, 400);
  }, true);
  document.addEventListener('change', event => {
    if (!drawer.contains(event.target)) return;
    if (event.target.matches('[data-cart-quantity]')) {
      event.preventDefault(); event.stopImmediatePropagation();
      clearTimeout(quantityTimer);
      quantityTimer = null;
      pendingInput = null;
      const raw = event.target.value.trim();
      change(event.target, raw === '' ? NaN : Number(raw));
    } else if (event.target.matches('#sidebar_cart_conditions')) setTimeout(applyBusy, 0);
  }, true);
  document.addEventListener('keydown', event => {
    if (!opened) return;
    if (event.key === 'Escape') { event.preventDefault(); close(); return; }
    if (['Enter', ' '].includes(event.key) && event.target.matches('.cartTool-item[data-popup]')) {
      event.preventDefault(); event.target.click(); return;
    }
    if (event.key === 'Tab') {
      const nodes = [...drawer.querySelectorAll('a[href],button,input,[tabindex="0"]')].filter(node => !node.disabled && !node.hidden && node.getClientRects().length);
      const first = nodes[0], last = nodes[nodes.length - 1];
      if (!drawer.contains(document.activeElement)) { event.preventDefault(); first?.focus(); }
      else if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first?.focus(); }
    }
  }, true);
  retry.addEventListener('click', () => { notice = ''; refresh(); });
  drawer.addEventListener('transitionend', event => {
    if (event.target === drawer && opened && !drawer.contains(document.activeElement)) focusClose();
  });
  window.addEventListener('gp:add-to-cart-success', () => { notice = ''; open(document.activeElement); refresh(); });
  document.addEventListener('gp:open-cart-drawer', () => { notice = ''; open(document.activeElement); });
  ['cart-update', 'cart:updated', 'cart:change'].forEach(name => document.addEventListener(name, event => {
    const cart = event.detail?.cart || event.detail?.cartData || event.detail;
    updateCount(cart);
    if (opened && !mutationPromise) refresh();
  }));
  if (window.jQuery) window.jQuery(document).on('ajaxSuccess.lrlCartDrawer', (event, xhr, settings) => {
    const url = new URL(settings.url, location.origin);
    if (url.origin === location.origin && /\/cart\/add(?:\.js)?$/.test(url.pathname) && xhr.status >= 200 && xhr.status < 300) {
      notice = ''; open(document.activeElement); refresh();
    }
  });
  new MutationObserver(syncOpen).observe(document.body, {attributes: true, attributeFilter: ['class']});
  new MutationObserver(enhance).observe(wrapper, {childList: true, subtree: true});
  window.addEventListener('pageshow', () => { if (opened) refresh(); });
  window.addEventListener('focus', () => { if (opened) refresh(); });
  document.addEventListener('visibilitychange', () => { if (opened && !document.hidden) refresh(); });
  window.LRLCartDrawer = {version: 1, open, close, refresh};
  window.GPF ||= {};
  if (typeof window.GPF.openCartDrawer !== 'function') window.GPF.openCartDrawer = () => open(document.activeElement);
  enhance();
  syncOpen();
})();
